// Shared constants and helpers for the Serviços module.
// Extracted from ServicesPage.js during P2 refactor.

import { formatDateTenant } from "../../utils/tenantSettings";

export const STATUS_OPERACIONAL_OPTIONS = [
  { value: "aguardando", label: "Aguardando", color: "bg-slate-50 text-slate-700 border-slate-200" },
  { value: "acionado", label: "Enviado", color: "bg-sky-50 text-sky-700 border-sky-200" },
  { value: "iniciado", label: "Iniciado", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "em_origem", label: "Em Origem", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "em_execucao", label: "Em Execução", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "em_destino", label: "Em Destino", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "finalizado", label: "Finalizado", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

export const STATUS_OPERACIONAL_MAP = STATUS_OPERACIONAL_OPTIONS.reduce((a, s) => { a[s.value] = s; return a; }, {});

export const STATUS_ORDER = STATUS_OPERACIONAL_OPTIONS.map((s) => s.value);

/**
 * Etapas que o serviço realmente tem. Mesmas regras da aba Status do drawer:
 * sem endereço de origem não existe "Em Origem", sem intermediário não existe
 * "Em Execução" e "Em Destino" só faz sentido com 2+ endereços.
 */
export function etapasDisponiveis(enderecos) {
  const end = (enderecos || []).filter(
    (e) => e && (e.logradouro || e.lat != null || e.lng != null),
  );
  const hasOrigem = end.some((e) => e.tipo === "origem") || end.length >= 1;
  const hasIntermediario = end.some((e) => e.tipo === "intermediario");
  const hasDestino = end.length >= 2 && end.some((e) => e.tipo === "destino");
  return STATUS_ORDER.filter((s) => {
    if (s === "em_origem") return hasOrigem;
    if (s === "em_execucao") return hasIntermediario;
    if (s === "em_destino") return hasDestino;
    return true;
  });
}

/** Próximo status válido (avança 1 passo, pulando etapas inexistentes). */
export function proximoStatus(atual, enderecos) {
  const etapas = etapasDisponiveis(enderecos);
  const rank = STATUS_ORDER.indexOf(atual);
  if (rank < 0) return null;
  return etapas.find((s) => STATUS_ORDER.indexOf(s) > rank) || null;
}

/** Status anterior válido (retrocede 1 passo). */
export function statusAnterior(atual, enderecos) {
  const etapas = etapasDisponiveis(enderecos);
  const rank = STATUS_ORDER.indexOf(atual);
  if (rank <= 0) return null;
  const anteriores = etapas.filter((s) => STATUS_ORDER.indexOf(s) < rank);
  return anteriores.length ? anteriores[anteriores.length - 1] : null;
}

export const ENDERECO_TIPOS = [
  { value: "origem", label: "Origem" },
  { value: "destino", label: "Destino" },
  { value: "intermediario", label: "Intermediário" },
];

export const CALCULO_KM_LABELS = {
  base_enderecos_base: "Base → Endereços → Base",
  geo_enderecos_geo: "Geolocalização → Endereços → Geolocalização",
  enderecos_base: "Endereços → Base",
  enderecos_geo: "Endereços → Geolocalização",
};

// Auto-compute tipo from position in a multi-address service.
// 1 endereço → origem
// 2 endereços → origem + destino
// 3+ endereços → primeiro = origem, último = destino, demais = intermediario
export function computeTipo(index, total, multiplos) {
  if (!multiplos) return "destino"; // single-address type
  if (total === 1) return "origem";
  if (index === 0) return "origem";
  if (index === total - 1) return "destino";
  return "intermediario";
}

export function tipoLabelFromArray(idx, list, multiplos) {
  const tipo = computeTipo(idx, list.length, multiplos);
  if (tipo !== "intermediario") return ENDERECO_TIPOS.find((t) => t.value === tipo)?.label || tipo;
  let intCount = 0;
  for (let i = 1; i < list.length - 1; i++) {
    intCount++;
    if (i === idx) return `Intermediário #${intCount}`;
  }
  return "Intermediário";
}

export const EMPTY_FORM = {
  protocolo_externo: "",
  branch_id: "",
  base_poi_id: null,
  person_client_id: "",
  produto_id: "",
  service_type_id: "",
  modo_acionamento: "imediato",
  datetime_servico: "",
  previsao_chegada_minutos: "",
  datetime_previsao_chegada: "",
  datetime_inicio: "",
  datetime_fim: "",
  solicitante: "",
  solicitante_contato: "",
  solicitante_contatos: [],
  profissional_ids: [],
  viatura_ids: [],
  enderecos: [],
  veiculos: [],
  financeiro: [],
  custom_fields: {},
  required_equipments: {},
  status_operacional: "aguardando",
  observacoes: "",
  observacoes_visivel_profissional: true,
  tags: [],
};

export const genId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random()}`;

export const formatDateTime = (iso) => formatDateTenant(iso, { withTime: true });

// Value/financeiro status labels + colors (shared across Pessoas > Tabela de Valores and Serviços > Financeiro).
// Color scale follows workflow semantics: yellow → light green → blue → orange → dark green; red for dead-ends.
export const VALUE_STATUS_META = {
  em_analise:           { label: "Em Análise",           color: "#FF9945", bg: "#FFF1E5" }, // laranja do logotipo
  aprovada:             { label: "Aprovado",             color: "#65a30d", bg: "#ecfccb" }, // verde claro
  faturada:             { label: "Faturado",             color: "#3b82f6", bg: "#dbeafe" }, // azul — automático
  aguardando_pagamento: { label: "Aguardando Pagamento", color: "#ea580c", bg: "#ffedd5" }, // laranja — automático
  liquidada:            { label: "Liquidado",            color: "#16a34a", bg: "#dcfce7" }, // verde escuro — automático
  cancelada:            { label: "Cancelado",            color: "#991b1b", bg: "#fecaca" }, // vermelho escuro
};

// Keys in the order they should appear in Selects
export const VALUE_STATUS_KEYS = ["em_analise", "aprovada", "faturada", "aguardando_pagamento", "liquidada", "cancelada"];

// Escolhíveis pelo usuário: padrão da Tabela de Valores e troca no serviço.
// Faturado/Aguardando/Liquidado vêm do Faturamento e do Contas a Receber; Cancelado do fluxo de cancelamento.
export const VALUE_STATUS_SELECTABLE_PEOPLE = ["em_analise", "aprovada"];
export const VALUE_STATUS_SELECTABLE_SERVICES = ["em_analise", "aprovada"];

// Backwards-compat label map (used wherever we only need to render a label).
export const VALUE_STATUS_LABELS = Object.fromEntries(
  Object.entries(VALUE_STATUS_META).map(([k, v]) => [k, v.label])
);


// ───────────────────────── Vehicle field masks & validators ─────────────────────────

// Brazilian license plate mask.
// Accepts both old format (AAA-0000) and Mercosul format (AAA-0A00).
// Strips invalid chars, uppercases, inserts hyphen after 3rd character.
// Max 8 chars including hyphen.
export function formatPlaca(raw) {
  const cleaned = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
}

// Validate plate: AAA-0000 (old) OR AAA-0A00 (Mercosul).
export function isValidPlaca(plate) {
  if (!plate) return false;
  const s = plate.toUpperCase().replace("-", "");
  if (s.length !== 7) return false;
  const old = /^[A-Z]{3}\d{4}$/;
  const mercosul = /^[A-Z]{3}\d[A-Z]\d{2}$/;
  return old.test(s) || mercosul.test(s);
}

// Chassi pode vir incompleto: aceita até 17 caracteres, sem I/O/Q.
export function isValidChassi(chassi) {
  if (!chassi) return false;
  return /^[A-HJ-NPR-Z0-9]{1,17}$/.test(chassi.toUpperCase());
}

// Validate Renavam with mod 11 checksum (9 to 11 digits; we pad to 11).
export function isValidRenavam(renavam) {
  if (!renavam) return false;
  const digits = renavam.replace(/\D/g, "");
  if (digits.length < 9 || digits.length > 11) return false;
  // Reject all-zeros
  if (/^0+$/.test(digits)) return false;
  // Pad to 11 digits
  const padded = digits.padStart(11, "0");
  const base = padded.slice(0, 10);
  const dv = parseInt(padded[10], 10);
  // Reverse the 10 digits and multiply by [2,3,4,5,6,7,8,9,2,3]
  const reversed = base.split("").reverse().join("");
  const weights = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3];
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(reversed[i], 10) * weights[i];
  const mod = (sum * 10) % 11;
  const expected = mod === 10 ? 0 : mod;
  return expected === dv;
}

// ── Validação + montagem das listas de endereços/veículos ───────────────
// Usadas pelos sub-drawers e pelos formulários inline da tela única, para
// que as duas formas de cadastro apliquem exatamente as mesmas regras.

// Campos que definem o LOCAL do endereço — mesma régua do backend.
const ENDERECO_CAMPOS_LOCAL = ["cep", "logradouro", "numero", "bairro", "cidade", "uf", "complemento", "nome_local"];
const normLocal = (v) => String(v ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");

/** O endereço passou a ser outro ponto? (ignora pontuação/máscara) */
export function localEnderecoMudou(antigo, form) {
  if (!antigo) return false;
  return ENDERECO_CAMPOS_LOCAL.some((c) => normLocal(antigo[c]) !== normLocal(form[c]));
}

export function montarEnderecos({ form, enderecos, editingId, multiplos }) {
  if (form.cep && form.cep.replace(/\D/g, "").length !== 8) return { ok: false, erro: "CEP inválido" };
  if (!form.logradouro.trim()) return { ok: false, erro: "Logradouro obrigatório" };
  if (!form.numero.trim()) return { ok: false, erro: "Número obrigatório" };
  if (!form.bairro.trim()) return { ok: false, erro: "Bairro obrigatório" };
  if (!form.uf) return { ok: false, erro: "UF obrigatório" };
  if (!form.cidade) return { ok: false, erro: "Cidade obrigatória" };

  let next = editingId
    ? (enderecos || []).map((x) => (x.id === editingId ? { ...x, ...form } : x))
    : [...(enderecos || []), { id: genId(), ...form }];
  next = next.map((e, i) => ({ ...e, tipo: computeTipo(i, next.length, multiplos) }));
  return { ok: true, next };
}

export function montarVeiculos({ form, veiculos, editingId, campos, obrigatorios }) {
  // Sem o dict de obrigatórios (tipos antigos), todo campo ligado é obrigatório.
  const temDict = obrigatorios && Object.keys(obrigatorios).length > 0;
  const exige = (campo) => !!campos[campo] && (temDict ? !!obrigatorios[campo] : true);
  const vazio = (campo) => !(form[campo] || "").trim();

  if (exige("marca") && vazio("marca")) return { ok: false, erro: "Marca obrigatória" };
  if (exige("modelo") && vazio("modelo")) return { ok: false, erro: "Modelo obrigatório" };
  if (exige("ano") && vazio("ano")) return { ok: false, erro: "Ano do modelo obrigatório" };
  if (exige("cor") && vazio("cor")) return { ok: false, erro: "Cor obrigatória" };
  if (campos.placa) {
    if (exige("placa") && vazio("placa")) return { ok: false, erro: "Placa obrigatória" };
    if (!vazio("placa") && !isValidPlaca(form.placa)) return { ok: false, erro: "Placa inválida", detalhe: "Use o formato AAA-0000 (antiga) ou AAA-0A00 (Mercosul)." };
  }
  if (campos.chassi) {
    if (exige("chassi") && vazio("chassi")) return { ok: false, erro: "Chassi obrigatório" };
    if (!vazio("chassi") && !isValidChassi(form.chassi)) return { ok: false, erro: "Chassi inválido", detalhe: "Use até 17 caracteres, sem as letras I, O e Q." };
  }
  if (campos.renavam) {
    if (exige("renavam") && vazio("renavam")) return { ok: false, erro: "Renavam obrigatório" };
    if (!vazio("renavam") && !isValidRenavam(form.renavam)) return { ok: false, erro: "Renavam inválido", detalhe: "Verifique o número e o dígito verificador." };
  }

  const next = editingId
    ? (veiculos || []).map((x) => (x.id === editingId ? { ...x, ...form } : x))
    : [...(veiculos || []), { id: genId(), ...form }];
  return { ok: true, next };
}
